// Pipeline states + roles + colour mapping (mirrors backend models.py)
export const ACTIVE_STATES = [
  "Fresh",
  "RNR",
  "Follow-up Requested",
  "Out of Station",
  "Showroom Visit Booked",
  "Home Visit Booked",
  "Video Call Booked",
  "Showroom Visit Completed",
  "Home Visit Completed",
  "Video Call Completed",
  "Quotation Pending",
  "Price Negotiation",
];
export const TERMINAL_STATES = ["Deal Won", "Deal Lost", "Unwind Lead", "Junk"];
// Kanban column / dropdown display order (membership comes from ACTIVE_STATES + TERMINAL_STATES;
// this array just controls the left-to-right order shown on the Pipeline).
export const ALL_STATES = [
  "Fresh",
  "Showroom Visit Booked",
  "Home Visit Booked",
  "Video Call Booked",
  "Follow-up Requested",
  "Showroom Visit Completed",
  "Home Visit Completed",
  "Video Call Completed",
  "Quotation Pending",
  "Price Negotiation",
  "Deal Won",
  "Deal Lost",
  "Unwind Lead",
  "RNR",
  "Junk",
  "Out of Station",
];
export const DATE_REQUIRED_STATES = [
  "Follow-up Requested",
  "Showroom Visit Booked",
  "Home Visit Booked",
  "Video Call Booked",
];

export const STATE_STYLE = {
  Fresh: { badge: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  RNR: { badge: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  "Follow-up Requested": { badge: "bg-stone-100 text-stone-800 border-stone-300", dot: "bg-stone-900" },
  "Out of Station": { badge: "bg-yellow-50 text-yellow-800 border-yellow-200", dot: "bg-yellow-500" },
  "Showroom Visit Booked": { badge: "bg-indigo-50 text-indigo-800 border-indigo-200", dot: "bg-indigo-500" },
  "Home Visit Booked": { badge: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  "Video Call Booked": { badge: "bg-sky-50 text-sky-800 border-sky-200", dot: "bg-sky-500" },
  "Showroom Visit Completed": { badge: "bg-violet-50 text-violet-800 border-violet-200", dot: "bg-violet-600" },
  "Home Visit Completed": { badge: "bg-teal-50 text-teal-800 border-teal-200", dot: "bg-teal-600" },
  "Video Call Completed": { badge: "bg-cyan-50 text-cyan-800 border-cyan-200", dot: "bg-cyan-600" },
  "Quotation Pending": { badge: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200", dot: "bg-fuchsia-500" },
  "Price Negotiation": { badge: "bg-pink-50 text-pink-800 border-pink-200", dot: "bg-pink-500" },
  "Deal Won": { badge: "bg-green-50 text-green-800 border-green-200", dot: "bg-green-600" },
  "Deal Lost": { badge: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500" },
  "Unwind Lead": { badge: "bg-purple-50 text-purple-800 border-purple-200", dot: "bg-purple-600" },
  Junk: { badge: "bg-stone-100 text-stone-700 border-stone-300", dot: "bg-stone-400" },
};

export const STATE_HEX = {
  Fresh: "#10b981", RNR: "#f59e0b", "Follow-up Requested": "#3b82f6",
  "Out of Station": "#eab308",
  "Showroom Visit Booked": "#6366f1", "Home Visit Booked": "#f97316", "Video Call Booked": "#0ea5e9",
  "Showroom Visit Completed": "#7c3aed", "Home Visit Completed": "#0d9488", "Video Call Completed": "#06b6d4",
  "Quotation Pending": "#c026d3", "Price Negotiation": "#db2777",
  "Deal Won": "#16a34a", "Deal Lost": "#ef4444", "Unwind Lead": "#9333ea", Junk: "#a8a29e",
};

export const SEGMENT_STYLE = {
  "Trade / Architect": "bg-violet-50 text-violet-700 border-violet-200",
  "Project / Bulk": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "High Profile": "bg-amber-50 text-amber-700 border-amber-200",
  Retail: "bg-rose-50 text-rose-700 border-rose-200",
  Unknown: "bg-stone-50 text-stone-500 border-stone-200",
};

// ---- Lead sources ----
export const LEAD_SOURCES = ["Referral", "Repeat", "Friends & Family", "Walk-in", "Instagram"];
export const ALL_SOURCES = ["WATI", ...LEAD_SOURCES];

// Editable lead type (staff-set), separate from the AI-figured segment.
export const LEAD_TYPES = ["Retail", "B2B", "Architect", "Interior Designer", "OEM"];

// Editable per-lead requirement (what the customer wants), staff-set.
export const REQUIREMENTS = ["Sofa", "Bed", "Recliner", "Mattress", "Dining", "Console tables", "Full House", "Commercial Project"];

// ---- Stores (multi-tenancy) ----
// "Super Store" is the privileged tier — sees ALL stores' leads + AI features.
// Physical stores (HSR/BTM/…) scope a user/lead to a single shopfront and hide
// the AI surfaces entirely. Mirrors backend `STORE_SUPER`.
export const STORE_SUPER = "Super Store";
export const isSuperStore = (u) => (u?.store || STORE_SUPER) === STORE_SUPER;
// AI is allowed ONLY when role is permitted AND user is Super Store scope.
export const canViewAI = (u) =>
  !!u && CAN_VIEW_AI_ROLES.includes(u.role) && isSuperStore(u);
// Hot Leads view (explicit + AI Star) is restricted to Super Store managerial
// roles only — never shown to store-scoped users, and never to interaction /
// backend staff even when assigned to the Super Store.
export const canViewHotLeads = (u) =>
  !!u && VIEW_ALL_ROLES.includes(u.role) && isSuperStore(u);
// Activity Monitor — a Super Admin-only page (usage/audit dashboard).
export const isSuperAdmin = (u) => !!u && u.role === "super_admin";

// ---- Roles ----
export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  funnel_manager: "Funnel Manager",
  store_manager: "Store Manager",
  backend_staff: "Backend Staff",
  interaction_staff: "Interaction Staff",
};
export const ALL_ROLES = Object.keys(ROLE_LABELS);
export const VIEW_ALL_ROLES = ["super_admin", "admin", "funnel_manager", "store_manager"];
export const CAN_CREATE_ROLES = ["super_admin", "admin", "funnel_manager", "store_manager"];
export const CAN_ASSIGN_ROLES = ["super_admin", "admin", "funnel_manager", "store_manager"];
export const CAN_MARK_HOT_ROLES = ["super_admin", "admin", "funnel_manager", "store_manager", "backend_staff"];
// Funnel managers, store managers and admin can also be picked as the lead's
// backend (calling/chat) assignee — not just backend_staff.
export const BACKEND_ASSIGNABLE_ROLES = ["backend_staff", "funnel_manager", "store_manager", "admin", "super_admin"];
// Showroom-floor / home-visit / video-call interactions can now be owned by
// interaction staff, store managers, or admin (e.g. a flagship store admin
// running their own showroom visits).
export const INTERACTION_ASSIGNABLE_ROLES = ["interaction_staff", "store_manager", "admin", "super_admin"];
// Only admins / funnel / store managers see AI Research + AI-derived segment chip.
export const CAN_VIEW_AI_ROLES = ["super_admin", "admin", "funnel_manager", "store_manager"];
export const INTERACTION_ROLE = "interaction_staff";

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}
export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}
export function fmtINR(n) {
  if (n == null) return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}
